import React, { useState, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useHistory, useParams } from "react-router-dom";
import emojiStrip from "emoji-strip";
import moment from "moment";
import {
  Alert,
  BackButton,
  Breadcrumbs,
  Text,
  MandatoryFieldText,
  InputContainer,
  SendToast,
  Wrapper,
} from "@networkraildigitalfactory/components";
import { DatePicker } from "antd";

import { usePDP } from "../../hooks/useAPI";
import { a11yGrowTextarea, preventFormDefault } from "../../services/utils";
import { ExampleModal, AreYouSureModal } from "../Modals";
import StatusLabel from "../StatusLabel/StatusLabel";
import ValuesSelect from "../ValuesSelect/ValuesSelect";
import CreateActionButtons from "../CreateObjective/CreateActionButtons";
import EditActionButtons from "../CreateObjective/EditActionButtons";
import useUser from "../../hooks/useUser";

import styles from "../CreateObjective/CreateObjective.module.css";

const CreatePDP = (props) => {
  const { user, editMode, setLoading, isDirtyForm, setIsDirtyForm, setNavModalProps } = props;
  const history = useHistory();
  const params = useParams();
  const { getValues, control, clearError, register, errors, triggerValidation } = useForm({
    reValidateMode: "onChange",
  });
  const [isActionsDisabled, setIsActionsDisabled] = useState(false);
  const [isDeleteModalOpen, setIsCancelModalOpen] = useState(false);
  const [isExampleModalOpen, setIsExampleModalOpen] = useState(false);
  const [exampleModalFor, setExampleModalFor] = useState("objectiveDetail");
  const [pdpValues, setPdpValues] = useState();

  const devGoalRef = useRef(null);
  const actionRef = useRef(null);

  const { isGBR } = useUser();

  const [, getData] = usePDP({ url: params.id }, { manual: true });

  const fetchData = async () => {
    try {
      const result = await getData();
      if (result) {
        setPdpValues(result.data);
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
    }
  };

  useEffect(() => {
    if (editMode) {
      fetchData();
    }
  }, []);

  const goBack = () => {
    history.push("/my-objectives/PDP");
  };

  const switchToastMessage = (type) => {
    switch (type) {
      case "draft":
        return "Your development goal has been saved as a draft";
      case "edited":
        return "Updates have been saved to draft development goal";
      case "pending approval":
        return "Your development goal has been sent for approval";
      case "error":
        return "There was a problem updating development goal. Please try again.";
      default:
        return "";
    }
  };

  const [, postData] = usePDP(
    {
      method: "POST",
    },
    { manual: true }
  );

  const saveToCloud = async (data) => {
    try {
      data.email = user.email;
      await postData({ data });
    } catch (err) {
      console.error(err);
      SendToast(switchToastMessage("error"), "error");
    }
  };

  const onSubmit = async (data) => {
    const isValid = await triggerValidation();
    if (isValid) {
      setIsActionsDisabled(true);
      setLoading(true);
      data.status = "pending approval";
      await saveToCloud(data);
      setLoading(false);
      SendToast(switchToastMessage(data.status), "success");
      goBack();
    }
  };

  const onSave = async () => {
    setIsActionsDisabled(true);
    setLoading(true);
    const formData = getValues();
    formData.status = "draft";
    await saveToCloud(formData);
    setLoading(false);
    SendToast("Your development goal has been saved as a draft", "success");
    goBack();
  };

  const [{ error: errPut }, updateObjective] = usePDP(
    {
      url: params.id,
      method: "PUT",
    },
    { manual: true }
  );

  if (errPut)
    return SendToast("Your development goal hasn't been updated. Please try again", "error");

  const onEdit = async () => {
    const isValid = await triggerValidation();
    if (isValid) {
      try {
        setIsActionsDisabled(true);
        setLoading(true);
        await updateObjective({
          data: {
            ...pdpValues,
            status: "pending approval",
          },
        });
        setLoading(false);
        setIsDirtyForm(false);
        SendToast(switchToastMessage("pending approval"), "success");
        history.push(`/my-objectives/PDP`);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const onSaveDraft = async (e) => {
    setIsActionsDisabled(true);
    setLoading(true);
    try {
      e.preventDefault();
      const formData = getValues();
      await updateObjective({ data: formData });
      setLoading(false);
      setIsDirtyForm(false);
      SendToast("Your development goal has been saved as a draft", "success");
      history.push(`/my-objectives/PDP`);
    } catch (err) {
      console.error(err);
    }
  };

  const inputKeydown = () => {
    setIsDirtyForm(true);
  };

  const onInputChange = (field, value) => {
    setPdpValues((prevFormValues) => ({
      ...prevFormValues,
      [field]: emojiStrip(value),
    }));
    if (Object.keys(errors).length > 0) {
      triggerValidation();
    }
    setIsDirtyForm(true);
  };

  function disabledDate(current) {
    const today = new Date();
    return current && current < moment(today, "YYYY-MM-DD");
  }

  const getFormattedDate = () => {
    let date;
    if (pdpValues && pdpValues.dueDate === null) {
      date = undefined;
    } else {
      date = pdpValues && moment(new Date(pdpValues.dueDate), "DD/MM/YYYY");
    }
    return date;
  };

  const reValidate = () => {
    if (Object.keys(errors).length > 0) {
      triggerValidation();
    }
  };

  const getDropdownValue = () => {
    const selectedValue = pdpValues && pdpValues.value;
    return selectedValue;
  };

  const toggleDeleteModal = () => {
    setIsCancelModalOpen(!isDeleteModalOpen);
  };

  const switchExampleModal = (inputFor) => {
    if (isExampleModalOpen) {
      setIsExampleModalOpen(false);
    }
    setExampleModalFor(inputFor);
    setIsExampleModalOpen(true);
  };

  const toggleExampleModal = () => {
    setIsExampleModalOpen(!isExampleModalOpen);
  };

  const dueDateProps = {
    controler: {
      valueName: "dueDate",
      name: "dueDate",
      onChange: ([selected]) => {
        inputKeydown();
        clearError("dueDate");
        onInputChange("dueDate", selected.format(), false);
        return selected;
      },
      control,
      rules: {
        required: "Must not be blank",
        validate: () => {
          const dueDate = getValues("dueDate");
          const today = new Date();
          if (dueDate._d < today) {
            setLoading(false);
            setIsActionsDisabled(false);
            return "Due date cannot be in the past";
          }
          return true;
        },
      },
    },
    datePicker: {
      format: "DD/MM/YYYY",
      inputReadOnly: true,
      className: `${styles.form_input} ${errors.dueDate ? styles.error : ""}`,
      disabledDate,
      showNow: false,
      allowClear: false,
      onFocus: () => {
        reValidate();
      },
      getCalendarContainer: (trigger) => trigger.parentElement || document.body,
    },
  };

  const valueSelectProps = {
    name: "value",
    valueName: "value",
    onChange: ([selected]) => {
      inputKeydown();
      onInputChange("value", selected, false);
      return selected;
    },
    onSelect: reValidate,
    control,
    rules: {
      required: "Must not be blank",
    },
  };

  const breadcrumbs = [
    {
      text: "My Objectives",
      onClick: () => {
        if (isDirtyForm) {
          setNavModalProps({
            show: true,
            navPath: "/my-objectives/PDP",
          });
        } else {
          goBack();
        }
      },
    },
    {
      text: "New development goal",
    },
  ];

  const guidanceAfterSubmitting =
    "When you select to send for approval your development goal will be sent to your line manager. This gives them the opportunity to confirm and/or discuss further with you.";

  const GBR_BEHAVIOURAL_INFORMATION =
    "https://networkrail.sharepoint.com/sites/GBRTT-documents/People%20and%20Culture%20Public/Forms/AllItems.aspx?id=%2Fsites%2FGBRTT%2Ddocuments%2FPeople%20and%20Culture%20Public%2FCulture%20%2D%20Embed%20%28Lynn%20Chamberlain%2DClarke%29%2FBehavioural%20Capability%2FBehaviours%20%2D%20Quick%20Reference%20Tool%2Epdf&parent=%2Fsites%2FGBRTT%2Ddocuments%2FPeople%20and%20Culture%20Public%2FCulture%20%2D%20Embed%20%28Lynn%20Chamberlain%2DClarke%29%2FBehavioural%20Capability";

  return (
    <>
      <div className={styles.breadcrumbs_wrapper}>
        <Breadcrumbs items={breadcrumbs} />
      </div>
      <Wrapper maxWidth={1350} align="center">
        {isDirtyForm && (
          <Alert
            color="red"
            title="Unsaved Changes"
            message="You must save or submit your changes before navigating to another page. If you do not select save, all changes will be lost."
            style={{
              lineHeight: "140%",
              letterSpacing: "0.0015em",
              padding: "8px 10px 8px 10px",
            }}
          />
        )}
        <div className={styles.row}>
          <BackButton
            text="back to my development goals"
            onClick={() => {
              if (isDirtyForm) {
                setNavModalProps({
                  show: true,
                  navPath: "/my-objectives/PDP",
                });
              } else {
                goBack();
              }
            }}
            style={{ lineHeight: "140%", letterSpacing: "0.0015em" }}
          />
          {editMode && (
            <StatusLabel status={pdpValues && pdpValues.status !== "draft" ? "Editing" : "Draft"} />
          )}
        </div>
        <div className={styles.light_bg}>
          <Text
            type="header"
            wrapper="h2"
            style={{ lineHeight: "140%", letterSpacing: "0.0015em" }}
          >
            {editMode ? pdpValues && pdpValues.name : "New Development Goal"}
          </Text>
          <MandatoryFieldText />
          <form className={styles.form} role="presentation">
            <div className={styles.input_row}>
              <InputContainer
                labelText="Please give your development goal a name"
                exampleText="For example investigate project management role"
                forLabel="name"
                required
                errorText={errors.name && errors.name.message}
              >
                <input
                  name="name"
                  className={`${styles.objective_name} ${styles.form_input} ${
                    errors.name ? styles.error : ""
                  }`}
                  type="text"
                  onKeyDown={preventFormDefault}
                  ref={register({
                    required: "Must not be blank",
                  })}
                  defaultValue={editMode && pdpValues ? pdpValues.name : ""}
                  onChange={(e) => {
                    inputKeydown();
                    onInputChange("name", e.target.value, false);
                  }}
                />
              </InputContainer>
              <InputContainer
                labelText="Due date"
                exampleText="DD/MM/YYYY"
                forLabel="dueDate"
                required
                errorText={errors.dueDate && errors.dueDate.message}
              >
                {editMode && pdpValues && (
                  <Controller
                    as={
                      <DatePicker {...dueDateProps.datePicker} defaultValue={getFormattedDate()} />
                    }
                    {...dueDateProps.controler}
                    defaultValue={getFormattedDate()}
                  />
                )}
                {!editMode && (
                  <Controller
                    as={<DatePicker {...dueDateProps.datePicker} defaultValue={null} />}
                    {...dueDateProps.controler}
                    defaultValue={null}
                  />
                )}
              </InputContainer>
            </div>
            <InputContainer
              labelText={
                isGBR
                  ? "Please select the behaviour which most strongly supports this development goal"
                  : "Please select the Network Rail vision statement this development goal aligns to"
              }
              forLabel="value"
              required
              errorText={errors.value && errors.value.message}
            >
              {editMode && pdpValues && (
                <Controller
                  as={
                    <ValuesSelect
                      className={`${styles.form_input} ${errors.value ? styles.error : ""}`}
                      isGBR={isGBR}
                      isPDP
                    />
                  }
                  {...valueSelectProps}
                  defaultValue={getDropdownValue()}
                />
              )}
              {!editMode && (
                <Controller
                  as={
                    <ValuesSelect
                      className={`${styles.form_input} ${errors.value ? styles.error : ""}`}
                      isGBR={isGBR}
                      isPDP
                    />
                  }
                  {...valueSelectProps}
                  defaultValue={undefined}
                />
              )}
            </InputContainer>
            {isGBR && (
              <Text
                type="example"
                wrapper="p"
                style={{ marginTop: "-0.6rem", marginBottom: "2rem" }}
              >
                Click{" "}
                <a
                  href={GBR_BEHAVIOURAL_INFORMATION}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.gbrInstruction}
                >
                  here
                </a>{" "}
                to find more information about the behavioural framework.
              </Text>
            )}
            <Text
              wrapper="h3"
              type="subtitle"
              style={{ fontSize: "20px", lineHeight: "140%", letterSpacing: "0.0015em" }}
            >
              Setting your development goal
            </Text>
            <Text type="body" wrapper="p" size="16px">
              Development goals can be in support of your current role, potential future roles and
              career discussions. Development may not always be through formal training and could
              include mentoring, supporting or leading project work or a secondment opportunity.
            </Text>
            <div className={styles.textarea_wrapper}>
              <InputContainer
                labelText="Please describe your development goal"
                withExampleLink
                exampleLinkId="developmentGoalBtn"
                required
                forLabel="developmentGoal"
                errorText={errors.developmentGoal && errors.developmentGoal.message}
                onExampleClick={(e) => {
                  e.preventDefault();
                  switchExampleModal("developmentGoal");
                }}
              >
                <textarea
                  name="developmentGoal"
                  id="developmentGoal"
                  ref={
                    isExampleModalOpen
                      ? devGoalRef
                      : register({
                          required: "Must not be blank",
                        })
                  }
                  className={`${styles.form_textarea} ${
                    errors.developmentGoal ? styles.error : ""
                  }`}
                  defaultValue={editMode && pdpValues ? pdpValues.developmentGoal : ""}
                  onChange={(e) => {
                    inputKeydown();
                    onInputChange("developmentGoal", e.target.value, false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onInputChange("developmentGoal", e.target.value, true);
                    }
                    a11yGrowTextarea(e, "developmentGoal");
                  }}
                />
              </InputContainer>
              <InputContainer
                labelText="What’s your action plan? How will you achieve this?"
                withExampleLink
                exampleLinkId="actionPlanBtn"
                required
                forLabel="actionPlan"
                errorText={errors.actionPlan && errors.actionPlan.message}
                onExampleClick={(e) => {
                  e.preventDefault();
                  switchExampleModal("developmentActionPlan");
                }}
              >
                <textarea
                  name="actionPlan"
                  id="actionPlan"
                  ref={
                    isExampleModalOpen
                      ? actionRef
                      : register({
                          required: "Must not be blank",
                        })
                  }
                  className={`${styles.form_textarea} ${errors.actionPlan ? styles.error : ""}`}
                  defaultValue={editMode && pdpValues ? pdpValues.actionPlan : ""}
                  onChange={(e) => {
                    inputKeydown();
                    onInputChange("actionPlan", e.target.value, false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onInputChange("actionPlan", e.target.value, true);
                    }
                    a11yGrowTextarea(e, "actionPlan");
                  }}
                />
              </InputContainer>
            </div>
          </form>
        </div>
        <div className={styles.bottom_row}>
          <Text type="body" wrapper="span">
            {guidanceAfterSubmitting}
          </Text>
          <div className={styles.button_actions}>
            <div>
              {editMode ? (
                <EditActionButtons
                  isDirtyForm={isDirtyForm}
                  isActionsDisabled={isActionsDisabled}
                  status={
                    // eslint-disable-next-line no-nested-ternary
                    pdpValues && pdpValues.status !== "draft"
                      ? !isDirtyForm
                        ? "editing-no-change"
                        : "editing"
                      : "draft"
                  }
                  onSaveDraft={onSaveDraft}
                  toggleDeleteModal={toggleDeleteModal}
                  onEdit={() => {
                    onEdit(getValues());
                  }}
                />
              ) : (
                <CreateActionButtons
                  isDirtyForm={isDirtyForm}
                  isActionsDisabled={isActionsDisabled}
                  status="new"
                  saveObjective={onSave}
                  onSubmit={() => {
                    onSubmit(getValues());
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </Wrapper>
      <ExampleModal
        for={exampleModalFor}
        show={isExampleModalOpen}
        close={toggleExampleModal}
        shards={exampleModalFor === "actionPlan" ? [actionRef] : [devGoalRef]}
      />
      {editMode && (
        <AreYouSureModal
          show={isDeleteModalOpen}
          close={toggleDeleteModal}
          confirm={toggleDeleteModal}
          action="Delete"
          type="PDP"
          selectedId={params.id}
        />
      )}
    </>
  );
};

export default CreatePDP;
